import React, { useEffect, useRef, useState, JSX, CSSProperties } from 'react';
import { colors, css, fonts } from '../../../Styles';
import { createPopper, Placement } from '@popperjs/core';
import { ButtonTypes, DataValue, PopoverPosition } from '../../../Definitions';
import { createPortal } from 'react-dom';
import Overlay from '../overlay';
import NewButton from '../Buttons/newButton';

interface popoverProps {
  content: DataValue['value']
  contentFromTableButton?: boolean;
  popoverHeader?: string
  popoverContent?: JSX.Element
  overrides?: {
    popoverBgColor?: colors
    contentContainer?: CSSProperties
    popoverHeaderFontSize?: CSSProperties,
    popoverPosition?: PopoverPosition,
    btnStartEnhancer?: JSX.Element
  }
  createPopoverPortal?: boolean
  focusView?: boolean
}
/**
 * PrimaryPopover
 * @param content The element or text to trigger the popover on when clicked.
 * @param popoverHeader The title displayed in the header of the popover.
 * @param popoverContent The JSX content to render inside the popover body.
 * @param overrides Optional styles to customize the popover background color and container styling.
 * @returns A clickable element that shows a styled popover with a header and content when toggled.
 */
export default function PrimaryPopover({content, popoverHeader, contentFromTableButton, popoverContent, overrides, createPopoverPortal, focusView=false}:popoverProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);

  const _popoverBgColor: colors = overrides&&overrides?.popoverBgColor ? overrides.popoverBgColor : colors.blueDarkPrimary
  const _popoverHeaderFontSize = overrides && overrides?.popoverHeaderFontSize ? overrides.popoverHeaderFontSize : {...fonts.fontSize3};
  const _contentContainer = overrides&&overrides?.contentContainer ? overrides.contentContainer : {width: "fit-content", height: focusView ? "40px" : "fit-content"}
  let _popoverPosition: Placement = overrides?.popoverPosition || 'top';
  const popoverId = `popover-${Math.random().toString(36).substr(2, 9)}`;

  // #region | Create Popper
  useEffect(() => {
    if (buttonRef.current && tooltipRef.current && arrowRef.current) {
      createPopper(buttonRef.current, tooltipRef.current, {
        placement: _popoverPosition,
        modifiers: [
          { name: 'arrow', options: {
            element: arrowRef.current,
            padding: 0,
          }},
          { name: 'offset', options: {
            offset: [0, 20]
          }}
        ]
      });
    }
  }, []);
  // #endregion 

  // #region | Close the popover when clicking outside or on the content div
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current && !tooltipRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // #endregion

  // #region | Popper arrow style and placement
  const popperPlacement = (`
    .${popoverId} [data-popper-arrow] {
      position: absolute;
      width: 15px;
      height: 15px;
      z-index: -1;
      pointer-events: none;
    }
    .${popoverId} [data-popper-arrow]::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background: ${_popoverBgColor};
      transform: rotate(45deg);
      boxShadow: "1px 1px 4px #00000060";
    }
    .${popoverId}[data-popper-placement^='top'] [data-popper-arrow] {
      bottom: -9px;
    }
    .${popoverId}[data-popper-placement^='bottom'] [data-popper-arrow] {
      top: -10px;
    }
    .${popoverId}[data-popper-placement^='left'] [data-popper-arrow] {
      right: -5px;
    }
    .${popoverId}[data-popper-placement^='right'] [data-popper-arrow] {
      left: -5px;
    }
  `)
  // #endregion

  return ( <>
    {contentFromTableButton
      ? <NewButton
          ref={buttonRef}
          content={content}
          buttonType={ButtonTypes.tableButton}
          onClick={() => setShowTooltip(!showTooltip)} 
          startEnhancer={overrides&&overrides.btnStartEnhancer ? overrides.btnStartEnhancer : undefined}
        />
      : <div ref={buttonRef} onClick={() => setShowTooltip(!showTooltip)} style={{
      boxSizing: "border-box",
      ..._contentContainer
    }}>
      {content}
    </div>}
    {popoverContent ? ( !createPopoverPortal ? <>
      <style>{popperPlacement}</style>
      <div ref={tooltipRef} className={popoverId} style={{ 
        visibility: showTooltip ? 'visible' : 'hidden',
        opacity: showTooltip ? 1 : 0,
        transition: "opacity 0.2s ease-in-out, visibility 0.2s ease-in-out", 
        zIndex: 1000,
        backgroundColor: colors.greyDarkPrimary,
        border: "3px solid "+_popoverBgColor,
        borderRadius: '10px',
        maxWidth: '300px',
        position: "relative",
        backgroundClip: "padding-box",
        boxShadow: "1px 1px 4px #00000060"
      }}>
        <>
          { popoverHeader && <p style={{backgroundColor: _popoverBgColor, borderRadius: "7px 7px 0px 0px", width: "100%", height: "25px", ..._popoverHeaderFontSize, ...fonts.header, color: colors.greyLightTertiary, display: "flex", alignItems: "center", justifyContent: "center", padding: "0px 10px", boxSizing: "border-box", whiteSpace: "nowrap"}}>{popoverHeader}</p>}
          <div style={{padding: popoverHeader ? "5px 10px" : "10px", backgroundColor: colors.greyDarkPrimary, borderRadius: "0px 0px 7px 7px"}}>{popoverContent}</div>
        </>
        <div ref={arrowRef} data-popper-arrow/>
      </div>
    </> : createPortal( <>
      {showTooltip ? <Overlay onClick={() => setShowTooltip(false)}/> : <></>}
      <div style={{            
        boxSizing: "border-box",
        visibility: showTooltip ? 'visible' : 'hidden',
        opacity: showTooltip ? 1 : 0,
        zIndex: "99999",
        position: "absolute",
        bottom: "10px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "80%",
        maxWidth: "280px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px"
      }}>
        <div style={{boxSizing: "border-box", ..._contentContainer}}>
          {content}
        </div>  
        <div style={{boxSizing: "border-box", border: "3px solid "+_popoverBgColor, borderRadius: '10px', backgroundColor: colors.greyDarkPrimary, height: "fit-content", width: "100%"}}>
          <p style={{backgroundColor: _popoverBgColor, borderRadius: "7px 7px 0px 0px", width: "100%", height: "25px", ...fonts.fontSize3, ...fonts.header, color: colors.greyLightTertiary, display: "flex", alignItems: "center", justifyContent: "center", padding: "0px 10px", boxSizing: "border-box", whiteSpace: "nowrap"}}>{popoverHeader}</p>
          <div style={{padding: "5px 10px", backgroundColor: colors.greyDarkPrimary, borderRadius: "0px 0px 7px 7px"}}>{popoverContent}</div>
        </div>
      </div>
    </>, document.body)) : undefined}
  </>)
}